import {
  DateRangeDropdown,
  Dropdown,
  FilterContext,
  Typology,
} from '@/components';
import { useGetRequestsClaimants } from '@/modules/Requests/queries/get-requests-claimants';
import {
  FilterType,
  OrderByTypeValues,
  StatusFilter,
  TypologyValue,
} from '@/shared';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const filterTypeValues = Object.values(FilterType).filter(
  (value) => typeof value === 'number',
) as number[];

const orderByTypeValues = Object.values(OrderByTypeValues).filter(
  (value) => typeof value === 'string',
) as string[];

const statusValues = Object.values(StatusFilter)
  .filter((value) => typeof value === 'number')
  .map((value) => value) as number[];

export const KanbanFilter = () => {
  const { t } = useTranslation();
  const {
    activeTypologies,
    toggleTypology,
    filterBy,
    sortBy: orderBy,
    dateRange,
    setFilterBy,
    setFilter,
    setSortBy,
    setDateRange,
    totalByTypology,
  } = useContext(FilterContext);

  const [currentRequiresActionValue, setCurrentRequiresActionValue] =
    useState<string>();
  const [currentClaimant, setCurrentClaimant] = useState<number>();
  const [currentStatus, setCurrentStatus] = useState<number>();

  const requiresActionOptions = useMemo(
    () => [
      { value: 'true', label: t('yes') },
      { value: 'false', label: t('no') },
    ],
    [t],
  );

  const statusOptions = statusValues.map((value) => ({
    value,
    label: t(`status_filter.${StatusFilter[value]}`),
  }));

  const { data: requestClaimants } = useGetRequestsClaimants();

  const isTypologyDisabled = useCallback(
    (typology: TypologyValue) => {
      return !activeTypologies.includes(`${typology}`);
    },
    [activeTypologies],
  );

  const categoryInfo = useMemo(
    () => [
      {
        type: TypologyValue.Discovery,
        counter: totalByTypology?.[TypologyValue.Discovery],
      },
      {
        type: TypologyValue.Development,
        counter: totalByTypology?.[TypologyValue.Development],
      },
      {
        type: TypologyValue.Diligence,
        counter: totalByTypology?.[TypologyValue.Diligence],
      },
    ],
    [totalByTypology],
  );

  const filterTypeOptions = useMemo(
    () =>
      filterTypeValues.map((value) => ({
        value,
        label: t(`filter_type.${FilterType[value]}`),
      })),
    [t],
  );

  const orderByTypeOptions = useMemo(
    () =>
      orderByTypeValues.map((value) => ({
        value,
        label: t(`order_by_type_${value}`),
      })),
    [t],
  );

  const numberDropdownOptions = useMemo(() => {
    switch (filterBy) {
      case FilterType.Claimant:
        return {
          options:
            requestClaimants?.map((claimant) => ({
              value: claimant.id,
              label: claimant.fullName,
            })) ?? [],
          value: currentClaimant,
          setValue: (value?: number) => {
            setCurrentClaimant(value);
            setFilter(value?.toString());
          },
          onRemoveAllSelectedClick: () => {
            setCurrentClaimant(undefined);
            setFilter(undefined);
          },
        };
      case FilterType.Status:
        return {
          options: statusOptions,
          value: currentStatus,
          setValue: (value?: number) => {
            setCurrentStatus(value);
            setFilter(value?.toString());
          },
          onRemoveAllSelectedClick: () => {
            setCurrentStatus(undefined);
            setFilter(undefined);
          },
        };
    }
  }, [
    currentClaimant,
    currentStatus,
    filterBy,
    requestClaimants,
    setFilter,
    statusOptions,
  ]);

  const requiredActionsDropdownSettings = useMemo(() => {
    return (
      filterBy === FilterType.RequireAction && {
        options: requiresActionOptions,
        value: currentRequiresActionValue,
        setValue: (value?: string) => {
          setCurrentRequiresActionValue(value);
          setFilter(value);
        },
        onRemoveAllSelectedClick: () => {
          setCurrentRequiresActionValue(undefined);
          setFilter(undefined);
        },
      }
    );
  }, [currentRequiresActionValue, filterBy, requiresActionOptions, setFilter]);

  const resetFilters = () => {
    setCurrentRequiresActionValue(undefined);
    setCurrentClaimant(undefined);
    setCurrentStatus(undefined);
    setFilter(undefined);
  };

  return (
    <div className="flex justify-between">
      <div className="flex gap-2.5">
        {categoryInfo.map((category, index) => (
          <Typology
            key={index}
            type={category.type}
            counter={category.counter}
            isDisabled={isTypologyDisabled(category.type)}
            onClick={() => toggleTypology(category.type)}
          />
        ))}
      </div>
      <div className="flex gap-10 text-sm font-black text-gray">
        <div className="flex items-center gap-2.5">
          <span>{t('filter')}</span>
          <Dropdown
            className="w-36"
            placeholder={t('select_filter')}
            options={filterTypeOptions}
            selectedValue={filterBy}
            onSelect={(value) => {
              setFilterBy(value);
              resetFilters();
            }}
            onRemoveAllSelectedClick={() => {
              setFilterBy(undefined);
              resetFilters();
            }}
          />
          {(!!numberDropdownOptions && (
            <Dropdown
              className="w-36"
              placeholder={t('filter_by')}
              options={numberDropdownOptions.options}
              selectedValue={numberDropdownOptions.value}
              onSelect={numberDropdownOptions.setValue}
              onRemoveAllSelectedClick={
                numberDropdownOptions.onRemoveAllSelectedClick
              }
              disabled={!numberDropdownOptions.options.length}
            />
          )) ||
            (requiredActionsDropdownSettings && (
              <Dropdown
                className="w-36"
                placeholder={t('filter_by')}
                options={requiredActionsDropdownSettings.options}
                selectedValue={requiredActionsDropdownSettings.value}
                onSelect={requiredActionsDropdownSettings.setValue}
                onRemoveAllSelectedClick={
                  requiredActionsDropdownSettings.onRemoveAllSelectedClick
                }
                disabled={!requiredActionsDropdownSettings.options.length}
              />
            )) ||
            (filterBy === FilterType.Time && (
              <DateRangeDropdown
                containerClassName="w-36"
                range={dateRange}
                onChange={setDateRange}
                placement="bottom-end"
                onRemoveAllSelectedClick={() => setDateRange(undefined)}
              />
            )) || (
              <Dropdown
                className="w-36"
                placeholder={t('filter_by')}
                options={[]}
                disabled
              />
            )}
        </div>
        <div className="flex items-center gap-2.5">
          <span>{t('order')}</span>
          <Dropdown
            className="w-36"
            placeholder={t('select_filter')}
            options={orderByTypeOptions}
            selectedValue={orderBy}
            onSelect={setSortBy}
            onRemoveAllSelectedClick={() => setSortBy(undefined)}
          />
        </div>
      </div>
    </div>
  );
};
